import { useEffect, useState } from 'react';
import apiClient from '../../Services/ApiClient';
import { decode } from '../../Services/DecodeToken';
import useGetCustomData from '../../Hooks/useGetCustomData';
import { USER_ROLE } from '../../Types';
import { LocationSelect } from '../../Components/Select/LocationSelect';
import { CompanySelect } from '../../Components/Select/CompanySelect';
import {
  Form,
  FormSection,
  SectionTitle,
  SectionDescription,
  FormGrid,
  FormTextField,
  FormCheckbox,
  FormFileUpload,
  FormRow,
} from '../../Components/Form';
import { ImageIcon } from 'lucide-react';

export const AssetCategoryForm: React.FC<any> = ({ onSubmit, onCancel, defaultValues }) => {
  const token: any = decode();
  const [CID] = useGetCustomData('/api/company/userregister');

  const [selectedCompany, setSelectedCompany] = useState<string>(
    defaultValues?.customer_id ? String(defaultValues.customer_id) : ''
  );
  const [selectedLocation, setSelectedLocation] = useState<string>(
    defaultValues?.location_id ? String(defaultValues.location_id) : ''
  );

  const companyIdForLocations = selectedCompany || (defaultValues?.customer_id ? String(defaultValues.customer_id) : '');
  const [locations] = useGetCustomData(
    companyIdForLocations ? `/api/location/${companyIdForLocations}` : ''
  );

  useEffect(() => {
    if (token.roles.id !== USER_ROLE.ADMIN_ABATIX && CID.length > 0 && !selectedCompany) {
      const defaultCID = CID[0].CID;
      setSelectedCompany(defaultCID);
      setFormData((prev: any) => ({ ...prev, customer_id: defaultCID }));
    }
  }, [token.roles.id, CID, selectedCompany]);

  const [formData, setFormData] = useState<any>({
    category_name: defaultValues?.category_name || '',
    description: defaultValues?.description || '',
    image: null,
    active_flag: defaultValues?.active_flag !== undefined ? defaultValues.active_flag : 1,
    customer_id: defaultValues?.customer_id || '',
    location_id: defaultValues?.location_id || '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev: any) => ({
        ...prev,
        [name]: checked ? 1 : 0,
      }));
    } else {
      setFormData((prev: any) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(formData);
    onCancel();
  };

  const [imagePreview, setImagePreview] = useState<string | null>(null);

  useEffect(() => {
    const fetchImage = async () => {
      if (!defaultValues?.id || imagePreview) return;

      try {
        const res = await apiClient(`/api/assets/image/category/${defaultValues.id}`);
        if (!res.ok) throw new Error('Failed to fetch image');
        const blob = await res.blob();
        setImagePreview(URL.createObjectURL(blob));
      } catch (err) {
        console.error('Image fetch error:', err);
      }
    };

    fetchImage();
  }, [defaultValues?.id, imagePreview]);

  const handleRemoveImage = () => {
    if (imagePreview?.startsWith('blob:')) URL.revokeObjectURL(imagePreview);
    setImagePreview(null);
    setFormData((prev: any) => ({ ...prev, image: null }));
  };

  return (
    <Form
      embedded
      hideTitle
      onSubmit={handleSubmit}
      title={defaultValues ? 'Edit Asset Category' : 'Add Asset Category'}
      subtitle="Configure asset categories with location assignment and optional image."
      submitLabel="Save Category"
      onCancel={onCancel}
      disableSubmit={!formData.customer_id}
    >
      <FormSection>
        <SectionTitle>Assignment</SectionTitle>
        <SectionDescription>Select the company and location for this category.</SectionDescription>
        <FormRow>
          {!defaultValues && (
            <CompanySelect
              companies={CID}
              selectedCompany={selectedCompany}
              defaultName="Select Company"
              onChange={(newCompany: any) => {
                setSelectedCompany(newCompany);
                setSelectedLocation('');
                setFormData((prev: any) => ({
                  ...prev,
                  customer_id: newCompany,
                  location_id: '',
                }));
              }}
            />
          )}
          <LocationSelect
            ovs={locations ?? []}
            selectedOvs={selectedLocation}
            defaultName="Select Location"
            onChange={(newLocation: any) => {
              setSelectedLocation(newLocation);
              setFormData((prev: any) => ({
                ...prev,
                location_id: newLocation ? Number(newLocation) : '',
              }));
            }}
            disabled={!defaultValues && !selectedCompany}
          />
        </FormRow>
      </FormSection>

      <FormSection>
        <SectionTitle>Category Details</SectionTitle>
        <FormGrid $columns={1}>
          <FormTextField
            label="Category Name"
            name="category_name"
            value={formData.category_name || ''}
            onChange={handleChange}
            required
          />
          <FormTextField
            label="Description"
            name="description"
            value={formData.description || ''}
            onChange={handleChange}
            multiline
            rows={3}
          />
        </FormGrid>
      </FormSection>

      <FormSection>
        <SectionTitle>Image</SectionTitle>
        <FormFileUpload
          accept="image/*"
          fileName={formData.image?.name}
          placeholder="Click to upload an image"
          subtext="PNG or JPG recommended"
          previewUrl={imagePreview}
          icon={<ImageIcon size={28} />}
          onChange={(file) => {
            if (file) {
              setFormData((prev: any) => ({ ...prev, image: file }));
              setImagePreview(URL.createObjectURL(file));
            }
          }}
          onRemove={handleRemoveImage}
        />
      </FormSection>

      <FormSection>
        <FormCheckbox
          name="active_flag"
          label="Active Status"
          checked={formData.active_flag === 1}
          onChange={handleChange}
        />
      </FormSection>
    </Form>
  );
};
